export interface DramaComparison {
  aspect: string;
  reality: string;
  drama: string;
}

export interface PrototypePerson {
  role: string;
  prototype: string;
  note: string;
}

export interface AnalysisDimension {
  label: string;
  score: number;
  note: string;
}

export interface Drama {
  id: string;
  title: string;
  subtitle: string;
  cover: string;
  category: string;
  tags: string[];
  year: number;
  platform: string;
  episodes: number;
  duration: string;
  hot: number;
  adaptationScore: number;
  confirmLevel: string;
  confirmNote: string;
  realEvent: string;
  eventTime: string;
  eventPlace: string;
  characters: PrototypePerson[];
  dimensions: AnalysisDimension[];
  comparisons: DramaComparison[];
  disclaimerRaw: string;
  disclaimerRead: string;
  summary: string;
  highlights: string[];
  editor: string;
  publishDate: string;
  readMinutes: number;
}

export const dramaCategories = [
  '经济犯罪',
  '社会纪实',
  '法治观察',
  '都市民生',
  '反诈前线',
  '科技伦理',
];

export const dramas: Drama[] = [
  {
    id: 'xuesezhangben',
    title: '血色账本',
    subtitle: '一本民间借贷账本，牵出一整条担保链',
    cover:
      'https://readdy.ai/api/search-image?query=A%20worn%20leather-bound%20financial%20ledger%20lying%20open%20on%20a%20weathered%20wooden%20desk%20surrounded%20by%20scattered%20banknotes%20and%20a%20brass%20lamp%20casting%20warm%20amber%20glow%2C%20dark%20moody%20cinematic%20film%20still%20with%20brick%20red%20and%20amber%20color%20grading%2C%20shallow%20depth%20of%20field%2C%20dramatic%20side%20lighting%2C%20high%20detail%20textured%20paper&width=900&height=1200&seq=cover-xuesezhangben&orientation=portrait',
    category: '经济犯罪',
    tags: ['民间借贷', '担保陷阱', '返乡创业'],
    year: 2025,
    platform: '竖屏短剧平台独播',
    episodes: 62,
    duration: '每集 2 分钟',
    hot: 96,
    adaptationScore: 82,
    confirmLevel: '已确认改编',
    confirmNote:
      '片方在片尾明确标注「改编自真实故事」，多家媒体核对后确认，剧情主干与华东某县的民间借贷纠纷高度吻合，人物已做化名与地域模糊处理。',
    realEvent:
      '2021 年前后，华东某县一名返乡创业青年为扩大养殖规模，先后向同村与邻镇多户借款，并为熟人签下连带担保。资金链断裂后债务层层传导，牵出数十名出借人，最终通过诉讼与调解分期清偿。',
    eventTime: '2021—2023 年',
    eventPlace: '华东某县',
    characters: [
      {
        role: '剧中「阿良」',
        prototype: '返乡创业青年（化名）',
        note: '保留了养殖创业、替友担保的核心经历，姓名与地域做了模糊处理。',
      },
      {
        role: '剧中「秦姐」',
        prototype: '借贷链条中间人',
        note: '剧中强化了其操控手段，真实情形更零散、更依赖熟人关系。',
      },
      {
        role: '剧中「老周」',
        prototype: '同村担保人',
        note: '由两名真实担保人的经历合并而成，属于常见的角色合并手法。',
      },
    ],
    dimensions: [
      { label: '事件框架还原度', score: 88, note: '借贷—担保—追偿的主干完全一致' },
      { label: '人物原型贴合度', score: 76, note: '核心人物有原型，但做了合并与强化' },
      { label: '时间线准确度', score: 70, note: '两年的事被压缩在数月内推进' },
      { label: '生活细节真实度', score: 64, note: '乡镇生活质感在线，个别桥段偏戏剧化' },
      { label: '法律程序严谨度', score: 58, note: '诉讼与调解流程被大幅简化' },
    ],
    comparisons: [
      {
        aspect: '起因',
        reality: '为扩大养殖规模向多户借款并替熟人担保，属典型民间借贷纠纷。',
        drama: '浓缩成一纸「生死担保」，冲突更集中，压迫感更强。',
      },
      {
        aspect: '债务规模',
        reality: '数十万元，出借人分散且多为乡邻。',
        drama: '放大为数百万元，突出层层转贷的窒息感。',
      },
      {
        aspect: '追债方式',
        reality: '以电话催收、上门沟通、诉前调解为主。',
        drama: '加入了泼漆、堵门等激烈桥段，属艺术加工。',
      },
      {
        aspect: '人物关系',
        reality: '多线并行的乡邻关系网。',
        drama: '合并为三五位核心角色，便于观众记忆。',
      },
      {
        aspect: '结局',
        reality: '通过诉讼与调解分期清偿，过程漫长。',
        drama: '以主角重整旗鼓收尾，励志色彩更浓。',
      },
    ],
    disclaimerRaw:
      '本剧根据真实事件改编，人物姓名、职业及部分情节已作艺术处理，如有雷同纯属巧合。',
    disclaimerRead:
      '这是爆料短剧里最常见的一档声明。「根据真实事件改编」确认取材真实；「人物姓名、职业」被改动，意味着剧中身份不能直接对应现实中的具体个人；「部分情节已作艺术处理」等于承认关键冲突存在虚构；「如有雷同纯属巧合」是免责兜底，法律上弱化指向性。读懂这三层，就能判断哪些可信、哪些当故事看。',
    summary:
      '《血色账本》是近年少见的把民间借贷链条拍清楚的作品。它没有虚构一个惊天大案，而是把乡镇熟人社会里「碍于情面签字」这个最日常的动作，拍成了压垮一个家庭的起点。也正因为切口小，它的真实感反而最强。',
    highlights: [
      '事件主干可核实，属于已确认改编',
      '人物为合并形象，不能按剧中姓名对号入座',
      '追债桥段存在明显戏剧强化',
    ],
    editor: '档案编辑组',
    publishDate: '2026-08-18',
    readMinutes: 9,
  },
  {
    id: 'guitulieche',
    title: '归途列车',
    subtitle: '一趟开往寻亲现场的慢车',
    cover:
      'https://readdy.ai/api/search-image?query=An%20empty%20rural%20railway%20platform%20at%20dusk%20with%20tracks%20stretching%20into%20warm%20orange%20haze%2C%20a%20single%20worn%20suitcase%20resting%20on%20the%20concrete%2C%20cinematic%20film%20still%20with%20warm%20amber%20and%20sepia%20color%20grading%2C%20no%20blue%20tones%2C%20soft%20golden%20light%2C%20melancholic%20atmosphere%2C%20high%20detail&width=900&height=1200&seq=cover-guitulieche&orientation=portrait',
    category: '社会纪实',
    tags: ['打拐寻亲', '团圆行动', '基层民警'],
    year: 2024,
    platform: '综合视频平台短剧场',
    episodes: 48,
    duration: '每集 3 分钟',
    hot: 89,
    adaptationScore: 74,
    confirmLevel: '高度疑似',
    confirmNote:
      '片方未在片尾使用「改编自真实事件」字样，仅标注「取材于社会新闻」。但关键情节与「团圆行动」期间的公开寻亲报道多处重合。',
    realEvent:
      '「团圆行动」开展期间，多地警方通过 DNA 比对帮助被拐人员找到亲生家庭。其中一起案例中，被拐者在养家长大成人，得知身世后自行购票返乡认亲，全程由基层民警陪同。',
    eventTime: '2021—2022 年',
    eventPlace: '中部省份多个市县',
    characters: [
      {
        role: '剧中「陈小满」',
        prototype: '被拐后寻亲的当事人',
        note: '经历与公开报道的寻亲者相似，但成长背景为剧情重新设计。',
      },
      {
        role: '剧中「老钟」',
        prototype: '基层民警',
        note: '集合了多名办案民警的形象，非单一个体。',
      },
    ],
    dimensions: [
      { label: '事件框架还原度', score: 78, note: '寻亲—比对—认亲的主线与报道一致' },
      { label: '人物原型贴合度', score: 66, note: '角色为多人拼合，个体特征不明确' },
      { label: '时间线准确度', score: 62, note: '现实中长达十年的等待被压缩' },
      { label: '生活细节真实度', score: 71, note: '县城与车站的生活质感还原较好' },
      { label: '法律程序严谨度', score: 60, note: 'DNA 比对流程做了通俗化简化' },
    ],
    comparisons: [
      {
        aspect: '取材方式',
        reality: '公开寻亲报道中的多个案例。',
        drama: '把多个案例缝合到一条主线，形成完整叙事。',
      },
      {
        aspect: '寻亲过程',
        reality: '以警方比对、民政协作为主，节奏缓慢。',
        drama: '加入当事人独自买票踏上归途的强戏剧线。',
      },
      {
        aspect: '认亲现场',
        reality: '多为克制、局促的真实反应。',
        drama: '被放大的情绪爆发，属于合理但明显的加工。',
      },
      {
        aspect: '配角设置',
        reality: '基层民警、民政工作人员、志愿者多人协作。',
        drama: '凝练为一两位关键角色推动剧情。',
      },
    ],
    disclaimerRaw: '本剧取材于社会新闻，剧情及人物均为艺术创作，请勿对号入座。',
    disclaimerRead:
      '注意这里的措辞是「取材于社会新闻」，而不是「根据真实事件改编」。前者含义更宽，只表示灵感来自真实报道，并不承诺哪段是真的；「剧情及人物均为艺术创作」进一步切断了对应关系。遇到这种声明，最稳妥的做法是把剧当独立作品看，不能拿它当事件记录。',
    summary:
      '《归途列车》最动人的地方不在认亲那一刻，而在漫长的等待。它把寻亲从「大新闻」还原成买车票、填表格、反复确认的琐碎过程。也正因如此，它与真实报道的距离不远，但绝不等于记录。',
    highlights: [
      '片方定位为「取材于社会新闻」，而非直接改编',
      '主角为多人经历拼合，无单一定型原型',
      '认亲段落的情绪强度高于真实记录',
    ],
    editor: '档案编辑组',
    publishDate: '2026-07-30',
    readMinutes: 8,
  },
  {
    id: 'chenmodedezhengci',
    title: '沉默的证词',
    subtitle: '一桩维权案，两份互相矛盾的笔录',
    cover:
      'https://readdy.ai/api/search-image?query=An%20empty%20old%20courtroom%20bench%20with%20wooden%20railings%20lit%20by%20warm%20afternoon%20sunlight%20through%20venetian%20blinds%2C%20dust%20particles%20floating%20in%20the%20air%2C%20cinematic%20film%20still%20with%20amber%20and%20brick%20red%20color%20grading%2C%20no%20blue%20tones%2C%20solemn%20quiet%20mood%2C%20high%20detail&width=900&height=1200&seq=cover-chenmozhengci&orientation=portrait',
    category: '法治观察',
    tags: ['劳动维权', '证据链', '法律援助'],
    year: 2025,
    platform: '竖屏短剧平台独播',
    episodes: 70,
    duration: '每集 2 分钟',
    hot: 84,
    adaptationScore: 69,
    confirmLevel: '部分取材',
    confirmNote:
      '片尾标注「部分情节参考公开案例」。剧情为多起维权事件的重组，无法对应到单一案件，属于典型的复合型取材。',
    realEvent:
      '数起劳动争议与工伤维权案例中，当事人因缺乏书面证据、证言前后不一致而陷入举证困境，最终在法律援助律师协助下完成调解或仲裁。',
    eventTime: '2019—2024 年',
    eventPlace: '多个城市',
    characters: [
      {
        role: '剧中「林素」',
        prototype: '多名维权当事人',
        note: '将数起案件中的当事人经历综合到一个角色身上。',
      },
      {
        role: '剧中「何律师」',
        prototype: '法律援助律师',
        note: '形象参考公开报道中的法援律师，属群像式塑造。',
      },
    ],
    dimensions: [
      { label: '事件框架还原度', score: 72, note: '举证困境与调解出口符合真实流程' },
      { label: '人物原型贴合度', score: 55, note: '角色为复合形象，个体原型不清晰' },
      { label: '时间线准确度', score: 64, note: '仲裁与诉讼周期被压缩明显' },
      { label: '生活细节真实度', score: 68, note: '工位、宿舍、法律援助中心还原度尚可' },
      { label: '法律程序严谨度', score: 74, note: '举证责任与证据规则表述较准确' },
    ],
    comparisons: [
      {
        aspect: '案件来源',
        reality: '多起彼此独立的维权事件。',
        drama: '重组为一桩贯穿全剧的核心案件。',
      },
      {
        aspect: '证据问题',
        reality: '真实难点是缺少书面凭证与证人出庭。',
        drama: '转化为「两份矛盾笔录」的悬疑钩子。',
      },
      {
        aspect: '对抗强度',
        reality: '以沟通、举证、调解为主，少见正面冲突。',
        drama: '加入了职场施压与舆论博弈线。',
      },
      {
        aspect: '结果',
        reality: '多数案件以调解或部分支持裁决结束。',
        drama: '给出更明确、更有力的胜诉结局。',
      },
    ],
    disclaimerRaw: '本剧部分情节参考公开案例，人物与情节经艺术加工，不代表任何具体事件。',
    disclaimerRead:
      '「部分情节参考公开案例」是最模糊的一种声明：它既不确认具体改编对象，也不否认取材真实。对观众来说，这等于告诉你「桥段可能来自真实，但不要把它们当成同一件事」。这类剧适合用来理解维权的一般流程，不适合用来判断某个具体案件的走向。',
    summary:
      '《沉默的证词》把镜头对准了维权者最无力的环节——举证。它没有把律师写成救世主，而是让流程本身成为主角。缺点是复合取材让它缺少一个真正扎得住的现实锚点。',
    highlights: [
      '属于多起案件重组，无单一定型原型',
      '法律流程表述相对准确，可作为科普参考',
      '结局比现实更明确，存在明显提纯',
    ],
    editor: '档案编辑组',
    publishDate: '2026-08-05',
    readMinutes: 10,
  },
  {
    id: 'wuyewaimai',
    title: '午夜外卖',
    subtitle: '凌晨两点，系统还在派单',
    cover:
      'https://readdy.ai/api/search-image?query=A%20rain-soaked%20city%20street%20at%20midnight%20with%20warm%20glowing%20shop%20signs%20and%20a%20delivery%20scooter%20parked%20at%20the%20curb%2C%20wet%20asphalt%20reflecting%20amber%20light%2C%20cinematic%20film%20still%20with%20warm%20orange%20and%20brick%20red%20color%20grading%2C%20no%20blue%20tones%2C%20moody%20urban%20atmosphere%2C%20high%20detail&width=900&height=1200&seq=cover-wuyewaimai&orientation=portrait',
    category: '都市民生',
    tags: ['骑手权益', '平台算法', '工伤认定'],
    year: 2025,
    platform: '综合视频平台短剧场',
    episodes: 56,
    duration: '每集 2 分钟',
    hot: 92,
    adaptationScore: 77,
    confirmLevel: '已确认改编',
    confirmNote:
      '片尾标注「改编自真实事件」，核心争议与公开报道的骑手工伤认定纠纷一致，配送平台名称与城市做了替换处理。',
    realEvent:
      '一起骑手在深夜配送途中发生意外的争议中，围绕劳动关系认定与工伤赔付产生分歧，最终进入仲裁与诉讼程序，引发对平台用工关系的广泛讨论。',
    eventTime: '2022—2024 年',
    eventPlace: '华南某市',
    characters: [
      {
        role: '剧中「阿凯」',
        prototype: '涉事骑手',
        note: '保留了深夜配送与事故的核心经历，姓名与平台做了替换。',
      },
      {
        role: '剧中「站长」',
        prototype: '配送站点负责人',
        note: '反映了基层站点的考核压力，但个体行为有夸张。',
      },
    ],
    dimensions: [
      { label: '事件框架还原度', score: 85, note: '事故—认定—仲裁主线与报道一致' },
      { label: '人物原型贴合度', score: 73, note: '主角有明确原型，配角做了群像化' },
      { label: '时间线准确度', score: 68, note: '维权周期被压缩到数周' },
      { label: '生活细节真实度', score: 86, note: '派单、限时、超时罚款等细节相当扎实' },
      { label: '法律程序严谨度', score: 66, note: '劳动关系认定表述基本准确但偏简化' },
    ],
    comparisons: [
      {
        aspect: '核心争议',
        reality: '劳动关系能否认定，直接决定赔付路径。',
        drama: '保留争议焦点，并加入更直观的庭审对峙。',
      },
      {
        aspect: '平台形象',
        reality: '以算法规则与用工模式呈现压力。',
        drama: '设置了具体的站点管理者作为对抗面。',
      },
      {
        aspect: '事故细节',
        reality: '公开报道较为简略。',
        drama: '对雨夜路况做了细致还原，属合理补白。',
      },
      {
        aspect: '结局',
        reality: '程序持续较久，结果并不戏剧化。',
        drama: '提供了更有情绪出口的收束。',
      },
    ],
    disclaimerRaw:
      '本剧改编自真实事件，涉及单位名称、人物身份均已作替换处理，剧情包含艺术创作成分。',
    disclaimerRead:
      '这一档声明比「如有雷同纯属巧合」更明确：它承认改编自真实事件，同时说明「单位名称、人物身份均已替换」。这类处理通常是为了规避名誉与商业秘密风险。观众可以据此理解真实事件的性质，但不应对应到身边具体的平台或站点。',
    summary:
      '《午夜外卖》最真实的不是事故，而是倒计时。它把平台派单的每一秒都拍得让人焦虑，这种日常化的压迫感，比任何激烈冲突都更接近骑手的真实处境。',
    highlights: [
      '核心争议真实可核实，属已确认改编',
      '平台与单位名称已替换，不可对号入座',
      '维权周期被明显压缩，结局更理想化',
    ],
    editor: '档案编辑组',
    publishDate: '2026-08-22',
    readMinutes: 9,
  },
  {
    id: 'huitoushian',
    title: '回头是岸',
    subtitle: '园区围墙内外的两种人生',
    cover:
      'https://readdy.ai/api/search-image?query=A%20high%20concrete%20compound%20gate%20at%20night%20under%20harsh%20warm%20floodlights%20with%20barbed%20wire%20on%20top%20and%20a%20lone%20guard%20post%2C%20cinematic%20film%20still%20with%20amber%20and%20brick%20red%20color%20grading%2C%20no%20blue%20tones%2C%20tense%20oppressive%20atmosphere%2C%20high%20detail%20texture&width=900&height=1200&seq=cover-huitoushian&orientation=portrait',
    category: '反诈前线',
    tags: ['电信诈骗', '劝返', '家庭救赎'],
    year: 2024,
    platform: '竖屏短剧平台独播',
    episodes: 66,
    duration: '每集 2 分钟',
    hot: 90,
    adaptationScore: 71,
    confirmLevel: '高度疑似',
    confirmNote:
      '片方在采访中称「故事来自多方见闻」，片尾仅标注「剧情为虚构创作」。整体框架与公开的反诈劝返报道相似，但缺少单一一手信源。',
    realEvent:
      '多地开展劝返工作期间，有被诱骗至境外从事电信诈骗的人员在家人与警方协作下返回。相关报道多聚焦劝返流程与家属配合，当事人经历细节少有公开。',
    eventTime: '2021—2023 年',
    eventPlace: '西南、华南边境地区',
    characters: [
      {
        role: '剧中「小雷」',
        prototype: '被诱骗的年轻人',
        note: '综合了多篇报道中对被诱骗者的一般描述。',
      },
      {
        role: '剧中「雷母」',
        prototype: '协助劝返的家属',
        note: '家属线为剧情重点，情绪渲染明显强于报道。',
      },
    ],
    dimensions: [
      { label: '事件框架还原度', score: 70, note: '诱骗—被困—劝返的路径与报道相符' },
      { label: '人物原型贴合度', score: 52, note: '无明确个体原型，属群像提炼' },
      { label: '时间线准确度', score: 58, note: '劝返周期被大幅压缩' },
      { label: '生活细节真实度', score: 67, note: '园区环境描写参考了公开通报' },
      { label: '法律程序严谨度', score: 63, note: '跨境协作流程做了通俗化处理' },
    ],
    comparisons: [
      {
        aspect: '信息来源',
        reality: '以官方通报与权威媒体报道为主，细节有限。',
        drama: '在有限信息基础上做了大量情境补白。',
      },
      {
        aspect: '劝返过程',
        reality: '多部门协作，周期长且不确定。',
        drama: '压缩为几次关键沟通，节奏紧凑。',
      },
      {
        aspect: '园区描写',
        reality: '公开信息多停留在环境与秩序层面。',
        drama: '加入了更具体的日常冲突与人物关系。',
      },
      {
        aspect: '结局',
        reality: '多数案例低调结束。',
        drama: '给出团圆式收尾，情绪价值更强。',
      },
    ],
    disclaimerRaw: '本剧剧情为虚构创作，人物、单位均无现实对应，仅用于反诈宣传警示。',
    disclaimerRead:
      '这是最「保守」的一档声明：直接写明「剧情为虚构创作」，等于放弃真实事件的对应关系。很多反诈题材短剧会采用这种处理，既能借用真实背景，又避免指向具体案例。看到这句话，就应该把它当作宣传片而不是事件还原。',
    summary:
      '《回头是岸》真正的价值在警示，而不在还原。它把「被高薪骗出去」这句老话，拍成了具体的选择与代价。它的虚构程度较高，但传递的信息并不失真。',
    highlights: [
      '片方声明为纯虚构创作，无单一原型',
      '园区与流程参考公开通报，细节多为补白',
      '作为反诈警示片观看更合适',
    ],
    editor: '档案编辑组',
    publishDate: '2026-06-28',
    readMinutes: 8,
  },
  {
    id: 'shuzilaolong',
    title: '数字牢笼',
    subtitle: '一条被反复转卖的通讯录',
    cover:
      'https://readdy.ai/api/search-image?query=A%20dim%20underground%20server%20room%20with%20tangled%20cables%20and%20a%20single%20amber%20warning%20light%20glowing%20on%20a%20rack%2C%20warm%20orange%20glow%20in%20darkness%2C%20cinematic%20film%20still%20with%20brick%20red%20and%20amber%20color%20grading%2C%20no%20blue%20tones%2C%20claustrophobic%20mood%2C%20high%20detail&width=900&height=1200&seq=cover-shuzilaolong&orientation=portrait',
    category: '科技伦理',
    tags: ['数据泄露', '隐私保护', '精准营销'],
    year: 2025,
    platform: '综合视频平台短剧场',
    episodes: 52,
    duration: '每集 2 分钟',
    hot: 81,
    adaptationScore: 80,
    confirmLevel: '已确认改编',
    confirmNote:
      '片尾标注「改编自公开报道的数据安全事件」，剧情主干与一起客户信息被内部人员导出并转卖的报道相符，企业与人物均已化名。',
    realEvent:
      '一起数据安全事件中，某企业内部人员利用职务便利导出大量客户通讯信息，并通过中间渠道多次转卖，最终流入营销公司用于电话推销，相关人员被依法处理。',
    eventTime: '2023—2025 年',
    eventPlace: '华北某市',
    characters: [
      {
        role: '剧中「小陶」',
        prototype: '涉事内部人员',
        note: '保留了利用权限导出数据的关键情节，姓名与岗位做了调整。',
      },
      {
        role: '剧中「老K」',
        prototype: '数据中间商',
        note: '将多名中间渠道联系人合并为一个角色。',
      },
    ],
    dimensions: [
      { label: '事件框架还原度', score: 86, note: '导出—转卖—推销的链条与报道一致' },
      { label: '人物原型贴合度', score: 72, note: '主犯有原型，中间环节做了合并' },
      { label: '时间线准确度', score: 74, note: '主要节点与报道基本对应' },
      { label: '生活细节真实度', score: 79, note: '办公与推销场景还原较真实' },
      { label: '法律程序严谨度', score: 70, note: '个人信息保护相关表述较准确' },
    ],
    comparisons: [
      {
        aspect: '事件性质',
        reality: '典型的内部人员数据泄露与转卖。',
        drama: '保留了链条，并强化了「数据即商品」的观感。',
      },
      {
        aspect: '传播规模',
        reality: '涉及一定数量的客户信息，具体数量未完整公开。',
        drama: '用可见的文件夹与进度条强化量级感。',
      },
      {
        aspect: '受害者视角',
        reality: '多数人只感到被骚扰，难以追溯来源。',
        drama: '设置了可追踪的受害者线索，便于叙事。',
      },
      {
        aspect: '结局',
        reality: '相关人员被依法处理，企业承担相应责任。',
        drama: '收束在主角的自省上，法律后果被简化。',
      },
    ],
    disclaimerRaw:
      '本剧改编自公开报道的数据安全事件，涉及企业、人物均已化名，部分情节为艺术创作。',
    disclaimerRead:
      '「改编自公开报道」加「已化名」，是目前数据题材较规范的处理方式：既承认取材真实，又提示信息来自公开报道而非一手信源。这类剧可以提供行业认知，但其中的技术细节多为可视化处理，不能当成完整的技术复盘。',
    summary:
      '《数字牢笼》把数据泄露从抽象的名词，变成了可以看见的东西：一个导出的表格，一次转手，一个不断响起的推销电话。它提醒观众，隐私的流失往往没有戏剧性的瞬间。',
    highlights: [
      '取材公开报道，主干可核实，属已确认改编',
      '企业与人物均已化名，不可对号入座',
      '技术细节为可视化处理，非完整复盘',
    ],
    editor: '档案编辑组',
    publishDate: '2026-09-02',
    readMinutes: 9,
  },
];